import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, Loader2 } from 'lucide-react';

import { ChartExportFilter } from '@/components/charts/types';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ChartExportButtonProps {
  onClick: () => void;
  isExporting: boolean;
  className?: string;
}

export const ChartExportButton: FC<ChartExportButtonProps> = ({
  onClick,
  isExporting,
  className,
}) => {
  const { t } = useTranslation();
  const tooltipText = t('charts.export.downloadImage');

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            onClick={onClick}
            disabled={isExporting}
            aria-label={tooltipText}
            className={cn(
              'absolute top-2 right-2 z-10 size-8 rounded-full border bg-background/70 shadow-sm backdrop-blur-sm',
              'opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-visible:opacity-100',
              isExporting && 'opacity-100',
              className
            )}>
            {isExporting ? (
              <Loader2 className='h-4 w-4 animate-spin' />
            ) : (
              <Download className='h-4 w-4' />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>{tooltipText}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

interface ChartExportHeaderProps {
  title: string;
  description?: string;
  activeTabLabel: string;
  exportFilters?: Array<ChartExportFilter>;
}

export const ChartExportHeader: FC<ChartExportHeaderProps> = ({
  title,
  description,
  activeTabLabel,
  exportFilters,
}) => (
  <div className='flex flex-col gap-4'>
    <div className='flex flex-col gap-1'>
      <p className='text-base font-semibold'>{title}</p>
      {description && (
        <p className='text-muted-foreground text-sm'>{description}</p>
      )}
      <p className='text-muted-foreground text-xs'>
        {activeTabLabel} · {new Date().toLocaleString()}
      </p>
    </div>
    {exportFilters && exportFilters.length > 0 && (
      <div className='flex flex-wrap items-center gap-1.5'>
        {exportFilters.map((filter) => (
          <span
            key={filter.label}
            className={cn(
              'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs',
              filter.active
                ? 'bg-muted/40 text-foreground'
                : 'border-dashed text-muted-foreground line-through'
            )}>
            {filter.label}
          </span>
        ))}
      </div>
    )}
  </div>
);
